import React, { useState, useRef, useEffect, useRef as useRefAlias } from "react";
import useGameBase from '../../hooks/useGameBase';
import SpectatorPerspectiveSelector from "./SpectatorPerspectiveSelector";
import { useSound } from '../../utils/sound/SoundProvider';

function SnapGame({ gameState, gamePlayers = [], user = {}, onGameAction, socket, roomId, isSpectator = false }) {
  // --- Spectator Perspective logic (modular, opt-in, matches Canasta) ---
  const perspectiveOptions = React.useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectivePlayer = React.useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  React.useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!Array.isArray(perspectiveOptions) || perspectiveOptions.length === 0) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  const { isLocalPlayerTurn, submitAction, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer });
  const sound = useSound();
  // For all per-player state, use the selected perspective
  const myId = React.useMemo(() => {
    for (const id of perspectiveIds) {
      if (gameState?.playerStacks && gameState.playerStacks[id]) return id;
    }
    return user?.socketId || null;
  }, [perspectiveIds, gameState?.playerStacks, user?.socketId]);
  const userId = myId;
  // Refs for player piles and central pile
  const playerPileRefs = useRef({});
  const centralPileRef = useRef(null);
  const myStack = React.useMemo(() => gameState.playerStacks?.[userId] || [], [gameState.playerStacks, userId]);
  const centralPile = React.useMemo(
    () => gameState.centralPile || [],
    [gameState.centralPile]
  );
  const isMyTurn = isLocalPlayerTurn();
  const canSnap = gameState.snapAvailable && centralPile.length >= 2;
  // scores are not used directly here
  const [snapPressed, setSnapPressed] = useState(false);

  // handlePlayCard is replaced by handlePlayCardAnimated

  function handleSnap() {
    submitAction({ type: "snap" }).catch(()=>{});
    setSnapPressed(true);
  }

  // Horizontal pile visualization: show only top 10 cards, offset each for horizontal effect
  const pileSize = myStack.length;
  const pileCards = myStack.slice(-52);
  // Card horizontal offset (reduce to fit more cards)
  const pileCardOffset = 5; // was 7
  const isFinished = !!gameState.finished;

  // Card back image path (ensure you have a card back image in public/cards/card_back.png)
  const cardBackImage = "/cards/card_back.png";

  // Helper to get card image path
  function getCardImage(card) {
    if (!card) return null;
    let value = card.value;
    let suit = card.suit;
    if (["J", "Q", "K", "A"].includes(value)) value = value.toLowerCase();
    return `/cards/${value}_of_${suit}.png`;
  }

  // Animation state for central pile angle and card movement
  const [angleAnimating, setAngleAnimating] = useState(false);
  const [angleAnimIdx, setAngleAnimIdx] = useState(null);
  const [moveCardAnim, setMoveCardAnim] = useState(false);
  const [movingCard, setMovingCard] = useState(null);
  const [movingPlayerId, setMovingPlayerId] = useState(null);
  const animTimeoutRef = useRef(null);
  const lastSnapEventNonceRef = useRef(null);
  const prevCentralPileRef = useRefAlias(centralPile);
  const prevPlayerStacksRef = useRefAlias(gameState.playerStacks);

  function handlePlayCard() {
    // Prevent playing a card if a snap is available
    if (gameState.snapAvailable) return;
    if (isMyTurn && myStack.length > 0) {
      submitAction({ type: "playCard" }).catch(()=>{});
      setSnapPressed(false);
    }
  }

  // Animation effect for all players
  useEffect(() => {
    const prevCentralPile = prevCentralPileRef.current;
    const prevPlayerStacks = prevPlayerStacksRef.current;
    // Detect if a card was added to the central pile
    if (centralPile.length > prevCentralPile.length) {
      // Find which player lost a card
      let foundPlayerId = null;
      let playedCard = null;
      for (const pid in prevPlayerStacks) {
        const prevStack = prevPlayerStacks[pid] || [];
        const currStack = gameState.playerStacks?.[pid] || [];
        if (currStack.length < prevStack.length) {
          // Card was played from this stack (from the end of the pile)
          foundPlayerId = pid;
          playedCard = prevStack[prevStack.length - 1];
          break;
        }
      }
      if (foundPlayerId && playedCard) {
        setMovingCard(playedCard);
        setMovingPlayerId(foundPlayerId);
        setMoveCardAnim(true);
        // Always trigger angle animation for previous top card
        if (centralPile.length > 1) {
          setAngleAnimating(true);
          setAngleAnimIdx(centralPile.length - 2);
          setTimeout(() => {
            setAngleAnimating(false);
            setAngleAnimIdx(null);
          }, 350); // match CSS transition duration
        }
        if (animTimeoutRef.current) clearTimeout(animTimeoutRef.current);
        animTimeoutRef.current = setTimeout(() => {
          setMoveCardAnim(false);
          setMovingCard(null);
          setMovingPlayerId(null);
        }, 400);
      }
    }
    prevCentralPileRef.current = centralPile;
    prevPlayerStacksRef.current = gameState.playerStacks;
  // Note: prevCentralPileRef and prevPlayerStacksRef are intentionally omitted from dependencies
  }, [centralPile, gameState.playerStacks, prevCentralPileRef, prevPlayerStacksRef]);

  // Preload card images for player's pile and central pile
  useEffect(() => {
    // Helper to preload a single image
    function preloadImage(src) {
      if (!src) return;
      const img = new window.Image();
      img.src = src;
    }
    // Preload top 3 cards of each pile (player and central)
    const pilesToPreload = [];
    // Player's own pile
    if (myStack && myStack.length > 0) {
      for (let i = Math.max(0, myStack.length - 3); i < myStack.length; i++) {
        const card = myStack[i];
        const src = getCardImage(card);
        if (src) pilesToPreload.push(src);
      }
    }
    // Central pile
    if (centralPile && centralPile.length > 0) {
      for (let i = Math.max(0, centralPile.length - 3); i < centralPile.length; i++) {
        const card = centralPile[i];
        const src = getCardImage(card);
        if (src) pilesToPreload.push(src);
      }
    }
    // Other players' piles
    if (gamePlayers && gameState.playerStacks) {
      for (const player of gamePlayers) {
        const stack = gameState.playerStacks[player.socketId] || [];
        if (stack.length > 0) {
          for (let i = Math.max(0, stack.length - 3); i < stack.length; i++) {
            const card = stack[i];
            const src = getCardImage(card);
            if (src) pilesToPreload.push(src);
          }
        }
      }
    }
    // Preload all collected images
    pilesToPreload.forEach(preloadImage);
  }, [myStack, centralPile, gamePlayers, gameState.playerStacks]);

  useEffect(() => {
    if (gameState.snapWinner) return undefined;
    const timer = setTimeout(() => {
      setSnapPressed(false);
    }, 250);
    return () => clearTimeout(timer);
  }, [centralPile.length, canSnap, gameState.snapWinner]);

  useEffect(() => {
    const snapEvent = gameState.lastSnapEvent;
    if (!snapEvent || !snapEvent.nonce) return;
    if (lastSnapEventNonceRef.current === snapEvent.nonce) return;

    lastSnapEventNonceRef.current = snapEvent.nonce;
    if (snapEvent.success) {
      sound?.play('snap:success');
      setSnapPressed(true);
      return;
    }

    sound?.play('snap:false');
    setSnapPressed(false);
  }, [gameState.lastSnapEvent, sound]);

  // No angle or 3D logic needed

  const snapWinnerName = gameState.snapWinner
    ? (() => {
        const winner = gamePlayers.find(
          (player) =>
            player.socketId === gameState.snapWinner ||
            player.userId === gameState.snapWinner
        );
        return winner ? winner.username : gameState.snapWinner;
      })()
    : null;



  // Highlight the perspective (spectator or local)
  const otherPlayers = gamePlayers.filter((player) => (player.socketId || player.userId || player.id) !== userId);
  const opponentRows = [];
  for (let i = 0; i < otherPlayers.length; i += 4) {
    opponentRows.push(otherPlayers.slice(i, i + 4));
  }

  const showSpectatorPerspectiveSelector = isSpectator && perspectiveOptions.length > 0;

  if (!gameState) {
    return (
      <div className="rounded-3xl bg-white p-6 text-gray-900 shadow-xl dark:bg-slate-900 dark:text-gray-100">
        {showSpectatorPerspectiveSelector && (
          <SpectatorPerspectiveSelector
            players={perspectiveOptions}
            value={spectatorViewPlayerId || ""}
            onChange={setSpectatorViewPlayerId}
          />
        )}
        Loading Snap...
      </div>
    );
  }

  return (
    <div
      className="w-full rounded-[30px] border border-emerald-900/40 p-4 text-white shadow-[0_24px_70px_rgba(0,0,0,0.22)] sm:p-6"
      style={{
        background:
          "radial-gradient(circle at top, rgba(255,255,255,0.12), transparent 30%), linear-gradient(180deg, #0f5d44 0%, #0b3f31 100%)",
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
      }}
    >
      {showSpectatorPerspectiveSelector && (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId || ""}
          onChange={setSpectatorViewPlayerId}
        />
      )}
      <style>{`
        @keyframes pulsate { 0% { transform: rotate(2deg) scale(1.00); } 25% { transform: rotate(0deg) scale(1.00); } 50% { transform: rotate(-2deg) scale(1.00); } 75% { transform: rotate(0deg) scale(1.00); }  100% { transform: rotate(2deg) scale(1.0); } }
        .pulsate-card { animation: pulsate 1.2s infinite; }
      `}</style>

      {/* <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mt-3 text-2xl font-black tracking-tight sm:text-3xl">{tableStatus}</div>
          <div className="mt-1 text-sm text-emerald-50/80">{tableHint}</div>
        </div>
      </div> */}


      <div className="mb-5 rounded-[28px] border border-white/10 bg-black/10 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-[2px] sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-100/75">Your Pile</div>
            
          </div>
          <div className="rounded-full bg-white/10 px-3 py-2 text-sm font-semibold text-emerald-50 shadow-inner">
            {pileSize} cards remaining
          </div>
        </div>

        {/* <div
          className="relative mt-4 mb-1 rounded-[24px] border border-white/10 bg-black/10 px-4 py-4"
          style={{ minHeight: "118px" }}
        > */}
          <div
            className="relative mx-auto mt-6 mb-4"
            style={{ width: `${40 + pileCards.length * pileCardOffset}px`, height: "90px" }}
            ref={el => { playerPileRefs.current[userId] = el; }}
          >
            {pileCards.map((card, idx) => {
              const isRightMost = idx === pileCards.length - 1;
              const canPulsate = !isFinished && isMyTurn && isRightMost && myStack.length > 0 && !gameState.snapAvailable;
              return (
                <div
                  key={idx}
                  className={`absolute w-16 h-24 rounded-lg bg-gray-200 dark:bg-gray-800 flex items-center justify-center text-xs shadow${canPulsate ? ' pulsate-card' : ''}`}
                  style={{
                    left: `${idx * pileCardOffset}px`,
                    top: "0px",
                    zIndex: idx,
                    boxShadow: `0 10px 24px rgba(0,0,0,0.22)`,
                    cursor: canPulsate ? 'pointer' : 'default',
                  }}
                  onClick={!isFinished && canPulsate ? handlePlayCard : undefined}
                >
                  <img
                    src={cardBackImage}
                    alt="Card back"
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                </div>
              );
            })}
          {/* </div> */}
        </div>
      </div>

      <div className="rounded-[30px] border border-white/10 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.12),transparent_38%),linear-gradient(180deg,rgba(0,0,0,0.12),rgba(0,0,0,0.22))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)] sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.24em] text-emerald-100/75">Central Pile</div>
            <div className="mt-1 text-sm text-emerald-50/75">
              {canSnap ? "Two matching ranks are exposed. Tap the pile now." : "Cards land here one at a time until a snap appears."}
            </div>
          </div>
          <div className="rounded-full bg-white/10 px-3 py-2 text-sm font-semibold text-emerald-50 shadow-inner">
            {centralPile.length} cards
          </div>
        </div>

        <div
          className="central-pile"
          style={{ margin: "24px 0", pointerEvents: isFinished ? 'none' : 'auto', opacity: isFinished ? 0.6 : 1 }}
          ref={centralPileRef}
          role="button"
          tabIndex={isFinished ? -1 : 0}
          aria-label="Call snap"
          onClick={isFinished ? undefined : handleSnap}
          onKeyDown={isFinished ? undefined : (e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleSnap();
            }
          })}
        >
          <div
            className="relative mx-auto mt-4 mb-2"
            style={{ width: `${40 + centralPile.length * 7}px`, height: "90px" }}
          >
            {centralPile.map((card, idx) => {
              // All but the latest card are stacked tightly (2px apart)
              // The latest card is spaced out (14px apart from the previous) and rotated clockwise
              let left = 0;
              let transform = "none";
              let transition = "none";
              let opacity = 1;
              let shake = false;
              let pulsate = false;
              // Animate previous top card (second from top) straightening and moving left
              if (centralPile.length > 1 && idx === centralPile.length - 2) {
                if (angleAnimating && angleAnimIdx === idx) {
                  left = idx * 2;
                  transform = "rotate(0deg)";
                  transition = "left 0.35s, transform 0.35s";
                } else {
                  left = idx * 2;
                  transform = "rotate(0deg)";
                  transition = "left 0.35s, transform 0.35s";
                }
              } else if (centralPile.length > 0 && idx === centralPile.length - 1) {
                // Top card always at angle and offset
                left = idx * 2 + 14;
                transform = "rotate(12deg)";
                transition = "left 0.35s, transform 0.35s";
                // Hide top card while moveCardAnim is active (animation in progress)
                if (moveCardAnim) {
                  opacity = 0;
                }
                // Shake if snap is available and not already pressed
                if (canSnap && !snapPressed) {
                  shake = true;
                  pulsate = true;
                }
              } else {
                left = idx * 2;
              }
              // Hide previous top card only during its animation out
              if (
                angleAnimating &&
                centralPile.length > 1 &&
                idx === centralPile.length - 2 &&
                !moveCardAnim
              ) {
                opacity = 0;
              }
              return (
                <div
                  key={idx}
                  className={`absolute w-16 h-24 rounded-lg bg-gray-200 dark:bg-gray-800 flex items-center justify-center text-xs shadow${shake ? ' shake-card' : ''}${pulsate ? ' pulsate-card' : ''}`}
                  style={{
                    left: `${left}px`,
                    top: "0px",
                    zIndex: idx,
                    boxShadow: `0 10px 24px rgba(0,0,0,0.24)`,
                    transform,
                    transition,
                    opacity,
                    cursor: 'pointer',
                  }}
                >
                  {getCardImage(card) ? (
                    <img
                      src={getCardImage(card)}
                      alt={`${card.value} of ${card.suit}`}
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  ) : (
                    <span>
                      {card.value} {card.suit[0].toUpperCase()}
                    </span>
                  )}
                </div>
              );
            })}
            {/* Animate the new card moving from the correct player's pile to central pile */}
            {!isFinished && moveCardAnim && movingCard && movingPlayerId && (() => {
              // Get start position from the correct player's pile (top for self, bottom row for others)
              let startX = 0, startY = 0;
              let endX = 0, endY = 0;
              if (movingPlayerId === userId) {
                const pileEl = playerPileRefs.current[userId];
                if (pileEl && centralPileRef.current) {
                  const pileRect = pileEl.getBoundingClientRect();
                  const centralRect = centralPileRef.current.getBoundingClientRect();
                  startX = pileRect.left - centralRect.left + (pileRect.width - 64);
                  startY = pileRect.top - centralRect.top;
                }
              } else {
                const pileEl = playerPileRefs.current[movingPlayerId];
                if (pileEl && centralPileRef.current) {
                  const pileRect = pileEl.getBoundingClientRect();
                  const centralRect = centralPileRef.current.getBoundingClientRect();
                  startX = pileRect.left - centralRect.left + (pileRect.width - 64);
                  startY = pileRect.top - centralRect.top;
                }
              }
              endX = (centralPile.length - 1) * 2 + 14;
              endY = 0;
              // Build keyframes string for animation
              const keyframes = [
                "@keyframes moveCardToPileDynamic {",
                `0% { left: ${startX}px; top: ${startY}px; transform: rotate(0deg); }`,
                `100% { left: ${endX}px; top: ${endY}px; transform: rotate(12deg); }`,
                "}"
              ].join("\n");
              return (
                <React.Fragment>
                  <style>{keyframes}</style>
                  <div
                    className="absolute w-16 h-24 rounded-lg bg-gray-200 dark:bg-gray-800 flex items-center justify-center text-xs shadow"
                    style={{
                      left: `${startX}px`,
                      top: `${startY}px`,
                      zIndex: 1000,
                      boxShadow: "0px 2px 8px rgba(0,0,0,0.18)",
                      transform: "rotate(0deg)",
                      transition: "none",
                      animation: "moveCardToPileDynamic 0.4s forwards",
                    }}
                  >
                    {getCardImage(movingCard) ? (
                      <img
                        src={getCardImage(movingCard)}
                        alt={`${movingCard.value} of ${movingCard.suit}`}
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <span>
                        {movingCard.value} {movingCard.suit[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                </React.Fragment>
              );
            })()}
          </div>
        </div>
        {snapPressed && gameState.snapWinner && (
          <>
            <div className="mb-2 flex items-center justify-center gap-2 rounded-2xl border border-emerald-300/25 bg-emerald-300/10 px-4 py-3 text-lg font-black text-emerald-100">
              SNAP!
              <span className="text-base font-semibold text-white/90">{snapWinnerName}</span>
            </div>
            {/* Animate central pile cards moving to winner's pile */}
            {centralPile.length > 0 && (() => {
              const winnerId = gameState.snapWinner;
              const winnerPileEl = playerPileRefs.current[winnerId];
              const centralEl = centralPileRef.current;
              if (!winnerPileEl || !centralEl) return null;
              const winnerRect = winnerPileEl.getBoundingClientRect();
              const centralRect = centralEl.getBoundingClientRect();
              // Animate each card in the central pile
              return centralPile.map((card, idx) => {
                // Stagger animation for each card
                const delay = idx * 0.08;
                // Start position: central pile
                const startX = centralRect.left - winnerRect.left + (idx * 2 + 14);
                const startY = centralRect.top - winnerRect.top;
                // End position: winner's pile (stacked horizontally)
                const endX = winnerRect.width - 64 + idx * 7;
                const endY = 0;
                const keyframes = [
                  `@keyframes moveSnapCard${idx} {`,
                  `0% { left: ${startX}px; top: ${startY}px; transform: rotate(12deg); opacity: 1; }`,
                  `100% { left: ${endX}px; top: ${endY}px; transform: rotate(0deg); opacity: 0.2; }`,
                  `}`
                ].join("\n");
                return (
                  <React.Fragment key={idx}>
                    <style>{keyframes}</style>
                    <div
                      className="absolute w-16 h-24 rounded-lg bg-green-200 dark:bg-green-800 flex items-center justify-center text-xs shadow"
                      style={{
                        left: `${startX}px`,
                        top: `${startY}px`,
                        zIndex: 2000 + idx,
                        boxShadow: "0px 2px 8px rgba(0,0,0,0.18)",
                        transform: "rotate(12deg)",
                        transition: "none",
                        animation: `moveSnapCard${idx} 0.5s ${delay}s forwards`,
                        opacity: 1,
                      }}
                    >
                      {getCardImage(card) ? (
                        <img
                          src={getCardImage(card)}
                          alt={`${card.value} of ${card.suit}`}
                          style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      ) : (
                        <span>
                          {card.value} {card.suit[0].toUpperCase()}
                        </span>
                      )}
                    </div>
                  </React.Fragment>
                );
              });
            })()}
          </>
        )}
      </div>

      <div className="mt-6 rounded-[28px] border border-white/10 bg-black/10 p-4 sm:p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-100/75">Opponents</div>
            
          </div>
        </div>

        {opponentRows.map((row, rowIdx) => (
          <div
            key={rowIdx}
            className={
              "w-full flex flex-row flex-wrap justify-center items-end gap-6 sm:gap-8" +
              (rowIdx > 0 ? " mt-10" : " mt-2")
            }
          >
            {row.map((player) => {
              const stack = gameState.playerStacks?.[player.socketId] || [];
              return (
                <div key={player.socketId} className="flex min-w-[150px] flex-col items-center rounded-2xl border border-white/10 bg-white/8 px-4 py-4 shadow-inner backdrop-blur-sm">
                  <div className="mb-2 text-center text-sm font-semibold text-white">{player.username}</div>
                  <div
                    className="relative"
                    style={{ width: `${Math.min(stack.length, 10) * 7 + 40}px`, height: "40px" }}
                    ref={el => { playerPileRefs.current[player.socketId] = el; }}
                  >
                    {Array.from({ length: Math.min(stack.length, 10) }).map((_, idx) => (
                      <img
                        key={idx}
                        src={cardBackImage}
                        alt="Card back"
                        className="absolute w-16 h-24 rounded-lg shadow"
                        style={{ left: `${idx * 2}px`, top: "0px", zIndex: idx, boxShadow: "0 10px 22px rgba(0,0,0,0.22)" }}
                      />
                    ))}
                  </div>
                  <div className="mt-20 rounded-full bg-black/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-100/80">
                    {stack.length} cards
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

SnapGame.Guide = function SnapGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Snap</h3>
      <ul className="list-disc ml-6">
        <li>Take turns playing cards to the central pile.</li>
        <li>If two cards of the same rank appear consecutively, be the first to snap!</li>
        <li>Don't make a mistake when snapping, or you'll burn a card.</li>
        <li>Collect all cards to win.</li>
      </ul>
    </div>
  );
};

export default SnapGame;
